import * as XLSX from 'xlsx';

export interface TaxInvoiceRow {
  id: string;           // 유니크 키 (승인번호)
  date: string;        // 작성일자
  supplierName: string; // 공급자 상호
  amount: number;       // 합계금액
  supplyAmount: number; // 공급가액
  tax: number;          // 세액 (면세는 0)
  type: '과세' | '면세';
}

export interface ParsedTaxResult {
  rows: TaxInvoiceRow[];
  type: '과세' | '면세';
}

export function detectType(filename: string): '과세' | '면세' {
  if (filename.includes('세금계산서')) return '과세';
  return '면세';
}

export function parseTaxInvoiceFile(file: File): Promise<ParsedTaxResult> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const data = new Uint8Array(e.target!.result as ArrayBuffer);
        const wb = XLSX.read(data, { type: 'array' });
        const ws = wb.Sheets[wb.SheetNames[0]];
        const raw: any[][] = XLSX.utils.sheet_to_json(ws, { header: 1 });

        // Row 4 텍스트로 과세/면세 판별 (파일명보다 정확)
        const row4Text = String(raw[4]?.[0] || '');
        const type: '과세' | '면세' = row4Text.includes('세금계산서') ? '과세' : detectType(file.name);

        // Row 5 = 헤더 (index 5)
        const headerRow = raw[5] as string[];
        if (!headerRow) { resolve({ rows: [], type }); return; }

        const colIdx: Record<string, number> = {};
        headerRow.forEach((h, i) => {
          const key = String(h || '').trim();
          // 공급자 상호는 첫 번째 '상호' 컬럼 (index < 공급받는자)
          if (key === '작성일자') colIdx['date'] = i;
          if (key === '승인번호') colIdx['approvalNo'] = i;
          if (key === '합계금액') colIdx['amount'] = i;
          if (key === '공급가액') colIdx['supply'] = i;
          if (key === '세액' && colIdx['tax'] === undefined) colIdx['tax'] = i;
          // 공급자 상호 = 첫 번째 '상호' (보통 index 6)
          if (key === '상호' && colIdx['supplier'] === undefined) colIdx['supplier'] = i;
        });

        const rows: TaxInvoiceRow[] = [];
        for (let i = 6; i < raw.length; i++) {
          const r = raw[i];
          if (!r || !r[colIdx['date']]) continue;

          const dateVal = String(r[colIdx['date']] || '');
          const supplierName = String(r[colIdx['supplier']] || '');
          const amount = Number(r[colIdx['amount']]) || 0;
          const supplyAmount = Number(r[colIdx['supply']]) || 0;
          const tax = colIdx['tax'] !== undefined ? (Number(r[colIdx['tax']]) || 0) : 0;

          if (!supplierName) continue;

          const approvalNo = String(r[colIdx['approvalNo']] || `${dateVal}-${supplierName}-${i}`);

          rows.push({
            id: `${type}-${approvalNo}`,
            date: dateVal,
            supplierName,
            amount,
            supplyAmount,
            tax,
            type,
          });
        }

        resolve({ rows, type });
      } catch (err) {
        reject(err);
      }
    };
    reader.onerror = () => reject(reader.error);
    reader.readAsArrayBuffer(file);
  });
}

// 업체별 월별 집계
export interface SupplierMonthly {
  supplierName: string;
  months: Record<string, { count: number; amount: number; supplyAmount: number; tax: number }>;
  total: { count: number; amount: number; supplyAmount: number; tax: number };
  type: '과세' | '면세';
}

export function aggregateBySupplierMonth(rows: TaxInvoiceRow[]): {
  suppliers: SupplierMonthly[];
  allMonths: string[];
} {
  if (!Array.isArray(rows) || rows.length === 0) {
    return { suppliers: [], allMonths: [] };
  }

  const map = new Map<string, SupplierMonthly>();

  rows.forEach(r => {
    const month = r.date.substring(0, 7); // YYYY-MM
    const key = `${r.supplierName}__${r.type}`;

    if (!map.has(key)) {
      map.set(key, {
        supplierName: r.supplierName,
        months: {},
        total: { count: 0, amount: 0, supplyAmount: 0, tax: 0 },
        type: r.type,
      });
    }

    const entry = map.get(key)!;
    if (!entry.months[month]) {
      entry.months[month] = { count: 0, amount: 0, supplyAmount: 0, tax: 0 };
    }
    entry.months[month].count += 1;
    entry.months[month].amount += r.amount;
    entry.months[month].supplyAmount += r.supplyAmount;
    entry.months[month].tax += r.tax;

    entry.total.count += 1;
    entry.total.amount += r.amount;
    entry.total.supplyAmount += r.supplyAmount;
    entry.total.tax += r.tax;
  });

  const suppliers = Array.from(map.values()).sort((a, b) =>
    b.total.amount - a.total.amount
  );

  const monthSet = new Set<string>();
  rows.forEach(r => monthSet.add(r.date.substring(0, 7)));
  const allMonths = Array.from(monthSet).sort();

  return { suppliers, allMonths };
}
